import {
  ButtonList,
  Dropdown,
  InputRichText,
  InputText,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import type {
  PropsWithChildren,
  PropsWithClassName,
} from 'lib-shared-types-utils';
import {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';

import type {
  FlowVariable,
  FlowVariableSelectorItemType,
} from './flowVariableSelector.types';

type VariableSelectorProps = PropsWithChildren &
  PropsWithClassName & {
    /** List of variables to be rendered */
    variables: FlowVariable[];
    /** If true, a search input will be rendered */
    withSearch?: boolean;
  };

/** A toolbar menu to enable selection of emendable variables in the editor */
export const FlowVariableSelector = ({
  className,
  variables,
  children,
  withSearch = variables.length > 10,
  ...otherProps
}: VariableSelectorProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { content: editorContent, window: editorWindow } = useContext(
    InputRichText.Context,
  );

  const handleSelect = useCallback(
    (variable: FlowVariableSelectorItemType) => {
      const variableNode = editorWindow.document.createTextNode(
        `{{${variable.key}}}`,
      );

      editorContent.insert(variableNode);
      setIsDropDownOpen(false);
    },
    [editorContent, editorWindow.document],
  );

  const filteredItems: FlowVariableSelectorItemType[] = useMemo(() => {
    return variables
      .filter(({ searchableName }: FlowVariable) =>
        variableLabelContainsSearchPhrase(searchableName, searchTerm),
      )
      .map(variableToSelectorItem);
  }, [variables, searchTerm]);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    [],
  );

  const toggleDropdown = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setIsDropDownOpen((isOpen) => !isOpen);
    inputRef.current?.focus();
  }, []);

  const rootClass = classnames(className);

  return (
    <div {...otherProps} className={rootClass}>
      <div onClick={toggleDropdown}>
        <InputRichText.ToolbarButton icon="dropdown-list" />
      </div>

      <Dropdown
        fixed
        isOpen={isDropDownOpen}
        onClose={() => setIsDropDownOpen(false)}
      >
        {withSearch && (
          <InputText
            ref={inputRef}
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search..."
            icon="search"
          />
        )}

        <ButtonList>
          {filteredItems.map((item) => (
            <ButtonList.Item
              key={item.id}
              onClick={() => handleSelect(item)}
              buttonProps={{ type: 'button' }}
            >
              {item.label}
            </ButtonList.Item>
          ))}
        </ButtonList>
      </Dropdown>
    </div>
  );
};

function variableToSelectorItem(
  variable: FlowVariable,
): FlowVariableSelectorItemType {
  return {
    ...variable,
    key: variable.key,
    id: typeof variable.key === 'string' ? variable.key : uuid(),
    name: variable.searchableName,
  };
}

function variableLabelContainsSearchPhrase(variableLabel: string, search = '') {
  return (
    variableLabel?.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !==
    -1
  );
}
